import * as yup from 'yup'

import {
  RbacBody,
  RbacPrivilege,
  RbacRoleCompany,
  RbacRoleTeam
} from 'types/api/rbac/rbac'

/**
 * Provide RBAC schemas that we validate in a unit test
 */

const ROLE_SCHEMA = yup
  .object({
    key: yup.string().required(),
    description: yup.string().required(),
    privileges: yup.array().of(yup.string()).required()
  })
  .required()
type RoleType = yup.InferType<typeof ROLE_SCHEMA>

const PRIVILEGE_SCHEMA = yup
  .object({
    key: yup.string().required(),
    description: yup.string().required()
  })
  .required()
type PrivilegeType = yup.InferType<typeof PRIVILEGE_SCHEMA>

/**
 * Validates that obj matches the passed in yup schema
 * and 'key' string field matches the object key string
 */
function validate(schema: yup.ObjectSchema<any>) {
  return (obj: Record<string, RoleType> | Record<string, PrivilegeType>) => {
    return (
      obj &&
      Object.entries(obj).every(
        ([key, value]) =>
          'key' in value && value.key === key && schema.isValidSync(value)
      )
    )
  }
}

export const RBAC_SCHEMA = yup.object({
  roles: yup
    .object({
      company: yup.object().required().test(validate(ROLE_SCHEMA)),
      team: yup.object().required().test(validate(ROLE_SCHEMA))
    })
    .required(),
  privileges: yup
    .object({
      company: yup.object().required().test(validate(PRIVILEGE_SCHEMA)),
      team: yup.object().required().test(validate(PRIVILEGE_SCHEMA))
    })
    .required()
})

/**
 * This static object is used as a fallback when fetching API endpoint 'roledefinitions'.
 * It's also used a test fixture.
 * It should stay in sync with the 'roledefinitions' response which comes from these files
 * in the gremlin service codebase:
 * - libraries/core/src/main/java/com/gremlininc/auth/models/Privilege.java
 * - libraries/core/src/main/java/com/gremlininc/auth/models/Role.java
 */
export const RBAC: RbacBody = {
  roles: {
    company: {
      [RbacRoleCompany.COMPANY_OWNER]: {
        key: RbacRoleCompany.COMPANY_OWNER,
        description:
          'Rollup role of all team and company roles. Granted all privileges. Must have at least 1 company owner.',
        privileges: [
          // Company
          RbacPrivilege.ACCESS_SECURITY_LOGS,
          RbacPrivilege.ACTIVATE_USER,
          RbacPrivilege.ADD_USER_TO_COMPANY,
          RbacPrivilege.ADD_USER_TO_COMPANY_WITHOUT_INVITE,
          RbacPrivilege.COMPANY_DEFAULT,
          RbacPrivilege.CREATE_REMOVE_TEAM,
          RbacPrivilege.DEACTIVATE_USER,
          RbacPrivilege.MANAGE_COMPANY_PREFS,
          RbacPrivilege.MANAGE_COMPANY_USER_PERMISSIONS,
          RbacPrivilege.MANAGE_INTEGRATIONS,
          RbacPrivilege.MANAGE_MFA,
          RbacPrivilege.MANAGE_OAUTH,
          RbacPrivilege.MANAGE_SAML,
          RbacPrivilege.MANAGE_TEST_SUITES,
          RbacPrivilege.PROMOTE_DEMOTE_OWNER,
          RbacPrivilege.READ_ALL_API_KEYS,
          RbacPrivilege.READ_ALL_CLIENTS,
          RbacPrivilege.READ_ALL_USERS,
          RbacPrivilege.UNSHARE_SCENARIO,

          // Team
          RbacPrivilege.ACTIVATE_API_KEY,
          RbacPrivilege.ACTIVATE_CLIENT,
          RbacPrivilege.ADD_REMOVE_USER_FROM_TEAM,
          RbacPrivilege.MANAGE_MONITORING_FOR_TEAM,
          RbacPrivilege.MANAGE_NOTIFICATION_SETTINGS_FOR_TEAM,
          RbacPrivilege.MANAGE_TEAM_CREDENTIALS,
          RbacPrivilege.MANAGE_TEAM_DETAILS,
          RbacPrivilege.MANAGE_TEAM_USER_PERMISSIONS,
          RbacPrivilege.MANAGE_WEBHOOKS,
          RbacPrivilege.READ_TEAM_ATTACKS,
          RbacPrivilege.READ_TEAM_CLIENTS,
          RbacPrivilege.READ_TEAM_SCENARIOS,
          RbacPrivilege.READ_TEAM_SCHEDULES,
          RbacPrivilege.READ_TEAM_USERS,
          RbacPrivilege.SHARE_ASSET,
          RbacPrivilege.SHARE_SCENARIO,
          RbacPrivilege.TEAM_DEFAULT
        ]
      },
      [RbacRoleCompany.COMPANY_MANAGER]: {
        key: RbacRoleCompany.COMPANY_MANAGER,
        description: 'Granted privileges to manage users and teams.',
        privileges: [
          RbacPrivilege.ACTIVATE_API_KEY,
          RbacPrivilege.ACTIVATE_CLIENT,
          RbacPrivilege.ACTIVATE_USER,
          RbacPrivilege.ADD_REMOVE_USER_FROM_TEAM,
          RbacPrivilege.ADD_USER_TO_COMPANY,
          RbacPrivilege.ADD_USER_TO_COMPANY_WITHOUT_INVITE,
          RbacPrivilege.COMPANY_DEFAULT,
          RbacPrivilege.CREATE_REMOVE_TEAM,
          RbacPrivilege.DEACTIVATE_USER,
          RbacPrivilege.MANAGE_COMPANY_USER_PERMISSIONS,
          RbacPrivilege.MANAGE_NOTIFICATION_SETTINGS_FOR_TEAM,
          RbacPrivilege.MANAGE_TEAM_CREDENTIALS,
          RbacPrivilege.MANAGE_TEAM_DETAILS,
          RbacPrivilege.MANAGE_TEST_SUITES,
          RbacPrivilege.READ_ALL_API_KEYS,
          RbacPrivilege.READ_ALL_CLIENTS,
          RbacPrivilege.READ_ALL_USERS,
          RbacPrivilege.READ_TEAM_CLIENTS,
          RbacPrivilege.READ_TEAM_USERS,
          RbacPrivilege.SHARE_ASSET,
          RbacPrivilege.SHARE_SCENARIO,
          RbacPrivilege.UNSHARE_SCENARIO
        ]
      },
      [RbacRoleCompany.COMPANY_ADMIN]: {
        key: RbacRoleCompany.COMPANY_ADMIN,
        description:
          'Auth and integration management, and access auditing privileges.',
        privileges: [
          RbacPrivilege.ACCESS_SECURITY_LOGS,
          RbacPrivilege.COMPANY_DEFAULT,
          RbacPrivilege.MANAGE_COMPANY_PREFS,
          RbacPrivilege.MANAGE_INTEGRATIONS,
          RbacPrivilege.MANAGE_MFA,
          RbacPrivilege.MANAGE_OAUTH,
          RbacPrivilege.MANAGE_SAML,
          RbacPrivilege.MANAGE_TEST_SUITES
        ]
      },
      [RbacRoleCompany.COMPANY_COORDINATOR]: {
        key: RbacRoleCompany.COMPANY_COORDINATOR,
        description: 'Create or remove teams',
        privileges: [
          RbacPrivilege.ADD_REMOVE_USER_FROM_TEAM,
          RbacPrivilege.ADD_USER_TO_COMPANY,
          RbacPrivilege.COMPANY_DEFAULT,
          RbacPrivilege.CREATE_REMOVE_TEAM,
          RbacPrivilege.MANAGE_COMPANY_USER_PERMISSIONS,
          RbacPrivilege.MANAGE_TEAM_USER_PERMISSIONS
        ]
      },
      [RbacRoleCompany.COMPANY_USER]: {
        key: RbacRoleCompany.COMPANY_USER,
        description: 'Login and password reset privileges.',
        privileges: [RbacPrivilege.COMPANY_DEFAULT]
      }
    },
    team: {
      [RbacRoleTeam.TEAM_MANAGER]: {
        key: RbacRoleTeam.TEAM_MANAGER,
        description: 'Team user, agent, integration and secret management.',
        privileges: [
          RbacPrivilege.ACTIVATE_API_KEY,
          RbacPrivilege.ACTIVATE_CLIENT,
          RbacPrivilege.ADD_REMOVE_USER_FROM_TEAM,
          RbacPrivilege.ADD_USER_TO_COMPANY,
          RbacPrivilege.MANAGE_MONITORING_FOR_TEAM,
          RbacPrivilege.MANAGE_NOTIFICATION_SETTINGS_FOR_TEAM,
          RbacPrivilege.MANAGE_TEAM_CREDENTIALS,
          RbacPrivilege.MANAGE_TEAM_DETAILS,
          RbacPrivilege.MANAGE_TEAM_USER_PERMISSIONS,
          RbacPrivilege.MANAGE_WEBHOOKS,
          RbacPrivilege.READ_ALL_USERS,
          RbacPrivilege.READ_TEAM_ATTACKS,
          RbacPrivilege.READ_TEAM_CLIENTS,
          RbacPrivilege.READ_TEAM_SCENARIOS,
          RbacPrivilege.READ_TEAM_SCHEDULES,
          RbacPrivilege.READ_TEAM_USERS,
          RbacPrivilege.SHARE_ASSET,
          RbacPrivilege.SHARE_SCENARIO,
          RbacPrivilege.TEAM_DEFAULT,
          RbacPrivilege.UNSHARE_SCENARIO
        ]
      },
      [RbacRoleTeam.TEAM_CREDENTIAL_MANAGER]: {
        key: RbacRoleTeam.TEAM_CREDENTIAL_MANAGER,
        description: 'Manage team credentials',
        privileges: [RbacPrivilege.MANAGE_TEAM_CREDENTIALS]
      },
      [RbacRoleTeam.TEAM_USER]: {
        key: RbacRoleTeam.TEAM_USER,
        description:
          'Create, run, and halt experiments, templates, schedules, team API management.',
        privileges: [
          RbacPrivilege.MANAGE_WEBHOOKS,
          RbacPrivilege.READ_TEAM_ATTACKS,
          RbacPrivilege.READ_TEAM_CLIENTS,
          RbacPrivilege.READ_TEAM_SCENARIOS,
          RbacPrivilege.READ_TEAM_SCHEDULES,
          RbacPrivilege.READ_TEAM_USERS,
          RbacPrivilege.TEAM_DEFAULT
        ]
      },
      [RbacRoleTeam.TEAM_VIEWER]: {
        key: RbacRoleTeam.TEAM_VIEWER,
        description: 'Read only access for a team',
        privileges: [
          RbacPrivilege.READ_TEAM_ATTACKS,
          RbacPrivilege.READ_TEAM_CLIENTS,
          RbacPrivilege.READ_TEAM_SCENARIOS,
          RbacPrivilege.READ_TEAM_SCHEDULES,
          RbacPrivilege.READ_TEAM_USERS
        ]
      }
    }
  },
  privileges: {
    company: {
      [RbacPrivilege.MANAGE_COMPANY_PREFS]: {
        key: RbacPrivilege.MANAGE_COMPANY_PREFS,
        description: 'Allows modification of Company preferences'
      },
      [RbacPrivilege.PROMOTE_DEMOTE_OWNER]: {
        key: RbacPrivilege.PROMOTE_DEMOTE_OWNER,
        description:
          'Allows promotion privileges of any company member to become an owner of their company'
      },
      [RbacPrivilege.ACTIVATE_USER]: {
        key: RbacPrivilege.ACTIVATE_USER,
        description: 'Allows activating a de-activated user'
      },
      [RbacPrivilege.MANAGE_MFA]: {
        key: RbacPrivilege.MANAGE_MFA,
        description:
          'Allows modification of company-wide multi-factor authentication configuration'
      },
      [RbacPrivilege.MANAGE_SAML]: {
        key: RbacPrivilege.MANAGE_SAML,
        description: 'Allows modification of SAML configuration'
      },
      [RbacPrivilege.COMPANY_DEFAULT]: {
        key: RbacPrivilege.COMPANY_DEFAULT,
        description: 'Allows login and password reset privileges'
      },
      [RbacPrivilege.MANAGE_COMPANY_USER_PERMISSIONS]: {
        key: RbacPrivilege.MANAGE_COMPANY_USER_PERMISSIONS,
        description: "Allows modification of a user's company-level roles"
      },
      [RbacPrivilege.UNSHARE_SCENARIO]: {
        key: RbacPrivilege.UNSHARE_SCENARIO,
        description: 'Allows unsharing a shared scenario'
      },
      [RbacPrivilege.MANAGE_OAUTH]: {
        key: RbacPrivilege.MANAGE_OAUTH,
        description: 'Allows modification of OAuth settings'
      },
      [RbacPrivilege.CREATE_REMOVE_TEAM]: {
        key: RbacPrivilege.CREATE_REMOVE_TEAM,
        description: 'Allows adding and deleting teams within the company'
      },
      [RbacPrivilege.MANAGE_INTEGRATIONS]: {
        key: RbacPrivilege.MANAGE_INTEGRATIONS,
        description: 'Allows read and write access to company-wide integrations'
      },
      [RbacPrivilege.ADD_USER_TO_COMPANY_WITHOUT_INVITE]: {
        key: RbacPrivilege.ADD_USER_TO_COMPANY_WITHOUT_INVITE,
        description: 'Allows creating a user without the need for an invite'
      },
      [RbacPrivilege.DEACTIVATE_USER]: {
        key: RbacPrivilege.DEACTIVATE_USER,
        description: 'Allows deactivating an active user'
      },
      [RbacPrivilege.ACCESS_SECURITY_LOGS]: {
        key: RbacPrivilege.ACCESS_SECURITY_LOGS,
        description: 'Allows read access to security logs'
      },
      [RbacPrivilege.READ_ALL_API_KEYS]: {
        key: RbacPrivilege.READ_ALL_API_KEYS,
        description:
          'Allows reading all API keys for all the users in the company'
      },
      [RbacPrivilege.ADD_USER_TO_COMPANY]: {
        key: RbacPrivilege.ADD_USER_TO_COMPANY,
        description: 'Allows adding new users to company'
      },
      [RbacPrivilege.READ_ALL_USERS]: {
        key: RbacPrivilege.READ_ALL_USERS,
        description: 'Allows reading all user information within the company'
      },
      [RbacPrivilege.READ_ALL_CLIENTS]: {
        key: RbacPrivilege.READ_ALL_CLIENTS,
        description: 'Allows reading all client information within the company'
      },
      [RbacPrivilege.MANAGE_TEST_SUITES]: {
        key: RbacPrivilege.MANAGE_TEST_SUITES,
        description: 'Allows management of custom test suites for all teams'
      }
    },
    team: {
      [RbacPrivilege.SHARE_ASSET]: {
        key: RbacPrivilege.SHARE_ASSET,
        description:
          'Allows sharing of team assets with other teams within the company'
      },
      [RbacPrivilege.READ_TEAM_SCENARIOS]: {
        key: RbacPrivilege.READ_TEAM_SCENARIOS,
        description: 'Allows reading all scenario information within a team'
      },
      [RbacPrivilege.MANAGE_TEAM_USER_PERMISSIONS]: {
        key: RbacPrivilege.MANAGE_TEAM_USER_PERMISSIONS,
        description: 'Allows modifications of a users team-level roles'
      },
      [RbacPrivilege.READ_TEAM_USERS]: {
        key: RbacPrivilege.READ_TEAM_USERS,
        description: 'Allows reading all user information within the team'
      },
      [RbacPrivilege.READ_TEAM_CLIENTS]: {
        key: RbacPrivilege.READ_TEAM_CLIENTS,
        description: 'Allows reading all agent information within the team'
      },
      [RbacPrivilege.TEAM_DEFAULT]: {
        key: RbacPrivilege.TEAM_DEFAULT,
        description:
          'Allows access to Gremlin experiment, templates, schedules, API keys'
      },
      [RbacPrivilege.ACTIVATE_CLIENT]: {
        key: RbacPrivilege.ACTIVATE_CLIENT,
        description: 'Allows activating a deactivated agent'
      },
      [RbacPrivilege.SHARE_SCENARIO]: {
        key: RbacPrivilege.SHARE_SCENARIO,
        description:
          'Allows sharing scenarios with other teams within the company'
      },
      [RbacPrivilege.ADD_REMOVE_USER_FROM_TEAM]: {
        key: RbacPrivilege.ADD_REMOVE_USER_FROM_TEAM,
        description: 'Allows removing a team member from the team'
      },
      [RbacPrivilege.READ_TEAM_ATTACKS]: {
        key: RbacPrivilege.READ_TEAM_ATTACKS,
        description: 'Allows reading all experiment information within a team'
      },
      [RbacPrivilege.MANAGE_NOTIFICATION_SETTINGS_FOR_TEAM]: {
        key: RbacPrivilege.MANAGE_NOTIFICATION_SETTINGS_FOR_TEAM,
        description: 'Allows modification of team-level notification settings'
      },
      [RbacPrivilege.MANAGE_MONITORING_FOR_TEAM]: {
        key: RbacPrivilege.MANAGE_MONITORING_FOR_TEAM,
        description: 'Allows modification of team-level monitoring settings'
      },
      [RbacPrivilege.READ_ALL_INTEGRATION_CLIENTS]: {
        key: RbacPrivilege.READ_ALL_INTEGRATION_CLIENTS,
        description:
          'Allows reading all integration client information within the company'
      },
      [RbacPrivilege.READ_COMPANY_RM_REPORTS]: {
        key: RbacPrivilege.READ_COMPANY_RM_REPORTS,
        description:
          'Allows reading all company reliability management and detected risks reports'
      },
      [RbacPrivilege.READ_TEAM_SCHEDULES]: {
        key: RbacPrivilege.READ_TEAM_SCHEDULES,
        description: 'Allows reading all schedule information within a team'
      },
      [RbacPrivilege.ACTIVATE_API_KEY]: {
        key: RbacPrivilege.ACTIVATE_API_KEY,
        description: 'Allows activating a deactivated API key'
      },
      [RbacPrivilege.MANAGE_WEBHOOKS]: {
        key: RbacPrivilege.MANAGE_WEBHOOKS,
        description: 'Allows modification of team webhooks'
      },
      [RbacPrivilege.MANAGE_TEAM_CREDENTIALS]: {
        key: RbacPrivilege.MANAGE_TEAM_CREDENTIALS,
        description:
          'Allows resetting team secrets, and rotating team certificates'
      },
      [RbacPrivilege.MANAGE_TEAM_DETAILS]: {
        key: RbacPrivilege.MANAGE_TEAM_DETAILS,
        description: 'Allows the modification of a teams details'
      }
    }
  }
}
